/**
 * The household rulebook (PRD 5.1). Reference material that the family agreed to,
 * living in D1 — config, not code. Editable here so changing a rule never needs
 * a deploy.
 */
import { useCallback, useState } from 'react';
import type { RulebookRule } from '../../shared/types';
import { api } from '../../lib/api';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Loading,
  PanelHeading,
  TextInput,
  errorText,
  useResource,
} from './ui';

function RuleRow({
  rule,
  index,
  count,
  busy,
  onSave,
  onMove,
  onRemove,
}: {
  rule: RulebookRule;
  index: number;
  count: number;
  busy: boolean;
  onSave: (id: number, text: string) => Promise<void>;
  onMove: (index: number, direction: -1 | 1) => void;
  onRemove: (rule: RulebookRule) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(rule.text);

  return (
    <Card as="li">
      {!editing ? (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sage-soft font-display text-sm font-semibold text-sage"
            >
              {index + 1}
            </span>
            <p className="max-w-prose leading-relaxed text-ink">{rule.text}</p>
          </div>
          <div className="flex flex-wrap gap-1">
            <Button
              variant="ghost"
              className="px-3"
              disabled={busy || index === 0}
              onClick={() => onMove(index, -1)}
              aria-label={`Move rule ${index + 1} up`}
            >
              <span aria-hidden="true">↑</span>
            </Button>
            <Button
              variant="ghost"
              className="px-3"
              disabled={busy || index === count - 1}
              onClick={() => onMove(index, 1)}
              aria-label={`Move rule ${index + 1} down`}
            >
              <span aria-hidden="true">↓</span>
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setText(rule.text);
                setEditing(true);
              }}
            >
              Edit
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => onRemove(rule)}>
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!text.trim()) return;
            await onSave(rule.id, text.trim());
            setEditing(false);
          }}
          className="space-y-3"
        >
          <Field label={`Rule ${index + 1}`}>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              required
              className="block w-full rounded-lg border border-border bg-surface p-3 text-base leading-relaxed text-ink focus-visible:border-sage focus-visible:ring-2 focus-visible:ring-sage/40 focus-visible:outline-none"
            />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={busy}>
              Save rule
            </Button>
            <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

export default function RulebookPanel() {
  const load = useCallback(() => api.get<RulebookRule[]>('/insights/rulebook'), []);
  const { data, loading, error, reload, setData } = useResource(load, []);

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const rules = data ?? [];

  async function save(id: number, text: string) {
    setBusy(true);
    setActionError(null);
    try {
      const updated = await api.put<RulebookRule>(`/insights/rulebook/${id}`, { text });
      setData(rules.map((r) => (r.id === id ? updated : r)));
    } catch (err) {
      setActionError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  /** Swap two rules' sort_order. Order is data, so it persists. */
  async function move(index: number, direction: -1 | 1) {
    const other = index + direction;
    if (other < 0 || other >= rules.length) return;

    const a = rules[index];
    const b = rules[other];
    setBusy(true);
    setActionError(null);
    try {
      const [updatedA, updatedB] = await Promise.all([
        api.put<RulebookRule>(`/insights/rulebook/${a.id}`, { sort_order: b.sort_order }),
        api.put<RulebookRule>(`/insights/rulebook/${b.id}`, { sort_order: a.sort_order }),
      ]);
      const next = rules.map((r) => (r.id === a.id ? updatedA : r.id === b.id ? updatedB : r));
      next.sort((x, y) => x.sort_order - y.sort_order || x.id - y.id);
      setData(next);
    } catch (err) {
      setActionError(errorText(err));
      reload();
    } finally {
      setBusy(false);
    }
  }

  async function remove(rule: RulebookRule) {
    if (!window.confirm(`Retire this rule?\n\n"${rule.text}"`)) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.del<RulebookRule>(`/insights/rulebook/${rule.id}`);
      setData(rules.filter((r) => r.id !== rule.id));
    } catch (err) {
      setActionError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function add(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const text = String(fd.get('text') ?? '').trim();
    if (!text) return;
    setBusy(true);
    setActionError(null);
    try {
      const created = await api.post<RulebookRule>('/insights/rulebook', { text });
      setData([...rules, created]);
      setAdding(false);
      form.reset();
    } catch (err) {
      setActionError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <PanelHeading
        title="House rules"
        sub="The rules we actually agreed to. They live in the database, not the code — change one here and it changes for good."
        action={
          rules.length > 0 ? (
            <Button variant="primary" onClick={() => setAdding((v) => !v)} aria-expanded={adding}>
              Add a rule
            </Button>
          ) : undefined
        }
      />

      {adding && (
        <Card className="mb-4">
          <form onSubmit={add} className="space-y-3">
            <Field label="New rule" hint="Write it the way you would say it at the kitchen table.">
              <TextInput name="text" required placeholder="No delivery services. Pick it up or cook it." />
            </Field>
            <div className="flex gap-2">
              <Button type="submit" variant="primary" disabled={busy}>
                Add rule
              </Button>
              <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {actionError && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-clay/30 bg-clay-soft px-3 py-2 text-sm text-clay"
        >
          {actionError}
        </p>
      )}

      {loading && <Loading label="Loading the rulebook" />}

      {!loading && error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && rules.length === 0 && !adding && (
        <EmptyState
          title="No rules written down yet"
          action={
            <Button variant="primary" onClick={() => setAdding(true)}>
              Write the first rule
            </Button>
          }
        >
          The rulebook is the household's agreement with itself — the things you decided once so you do
          not have to re-decide them every week. Start with the one you break most often.
        </EmptyState>
      )}

      {!loading && !error && rules.length > 0 && (
        <ul className="space-y-3">
          {rules.map((rule, index) => (
            <RuleRow
              key={rule.id}
              rule={rule}
              index={index}
              count={rules.length}
              busy={busy}
              onSave={save}
              onMove={move}
              onRemove={remove}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
